import { type FetchSocialFeedByFeedSlugAndChilizIdParams, useLocale, useSettings } from '@capsicum/common';
import { partnerFeedKeys } from '@capsicum/socios-core';
import { useCallback, useEffect, useMemo } from 'react';

import { useSocialFeedQuery } from '../data-access';
import { clearFeedPages, prepareFeedData } from '../util';

export const usePartnerFeed = (partnerId: string) => {
	const { locale } = useLocale();
	const {
		settings: { isPartnerGamesFeedEnabled, isPoblanoPartnerPageEnabled }
	} = useSettings();
	const key = partnerFeedKeys.partner(partnerId);
	const params: FetchSocialFeedByFeedSlugAndChilizIdParams = useMemo(
		() => ({
			chilizId: partnerId || '',
			feedSlug: isPartnerGamesFeedEnabled && isPoblanoPartnerPageEnabled ? 'partner_games' : 'partner_page',
			limit: 5,
			locale: locale
		}),
		[locale, partnerId, isPoblanoPartnerPageEnabled, isPartnerGamesFeedEnabled]
	);

	const {
		data,
		error,
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isLoading,
		refetch: refetchQuery
	} = useSocialFeedQuery(key, params as unknown as FetchSocialFeedByFeedSlugAndChilizIdParams);

	const { feedData, metadata } = prepareFeedData(data);

	const refetch = useCallback(async () => {
		clearFeedPages(key);
		await refetchQuery();
	}, [refetchQuery]);

	useEffect(() => {
		/* We need to refetch the query to reflect the feed changes in the partner page.
		 * Due to we have a stale time cache in the SocialFeedQuery
		 */
		refetch();
	}, [params, refetch]);

	return {
		data: feedData,
		error,
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isLoading,
		metadata,
		refetch
	};
};
